import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Crown, Users, FileText, Search, Plus, X,
  RefreshCcw, Mail, AlertTriangle, Ban, PlayCircle, Building2, UserPlus,
} from "lucide-react";

import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { buildCheckoutLinkBody, checkoutLinkErrorKey } from "./billingPanel";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const PLAN_META = {
  insight:  { Icon: Sparkles,    tone: "bg-zinc-50 text-zinc-700 border-zinc-200" },
  guardian: { Icon: ShieldCheck, tone: "bg-sky-50 text-sky-700 border-sky-200" },
  sentinel: { Icon: Crown,       tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function PlanBadge({ plan }) {
  const meta = PLAN_META[plan] || PLAN_META.guardian;
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium capitalize ${meta.tone}`}
      data-testid={`plan-badge-${plan}`}
    >
      <Icon size={11} strokeWidth={2.25} />
      {plan}
    </span>
  );
}

function StatusBadge({ status, t }) {
  const suspended = status === "suspended";
  const tone = suspended
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium ${tone}`}
      data-testid={`status-badge-${status}`}
    >
      {suspended ? <Ban size={11} strokeWidth={2.25} /> : <PlayCircle size={11} strokeWidth={2.25} />}
      {t(`status.${suspended ? "suspended" : "active"}`)}
    </span>
  );
}

function KpiCard({ label, value, Icon, testId, tone = "bg-white border-zinc-200" }) {
  return (
    <div
      className={`rounded-xl border ${tone} px-4 py-3 flex items-center gap-3`}
      data-testid={testId}
    >
      <div className="w-9 h-9 rounded-lg bg-zinc-50 flex items-center justify-center text-zinc-600">
        <Icon size={18} strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">{label}</p>
        <p className="text-lg font-semibold text-zinc-900" data-testid={`${testId}-value`}>{value}</p>
      </div>
    </div>
  );
}

function formatDate(iso, locale) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

function SubscriptionBadge({ status, periodEnd, locale, t }) {
  if (!status) return <span className="text-zinc-400">{t("billing.status_none")}</span>;
  const tone = status === "active" ? "bg-zinc-100 text-zinc-700 border-zinc-200"
    : status === "past_due" ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-zinc-50 text-zinc-500 border-zinc-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${tone}`}
          title={periodEnd ? formatDate(periodEnd, locale) : undefined}>
      {t(`billing.status_${status}`)}
    </span>
  );
}

export default function AdminPage() {
  const { t, i18n } = useTranslation("admin");
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [updatingPlanFor, setUpdatingPlanFor] = useState(null);
  const [updatingStatusFor, setUpdatingStatusFor] = useState(null);
  const [pendingSuspend, setPendingSuspend] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);

  // Create-and-invite form state
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPlan, setFormPlan] = useState("guardian");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Billing checkout-link form state
  const [billingMode, setBillingMode] = useState("upgrade");
  const [billingWorkspaceId, setBillingWorkspaceId] = useState("");
  const [billingPlan, setBillingPlan] = useState("guardian");
  const [billingInterval, setBillingInterval] = useState("month");
  const [billingEmail, setBillingEmail] = useState("");
  const [billingCompany, setBillingCompany] = useState("");
  const [billingSubmitting, setBillingSubmitting] = useState(false);
  const [billingError, setBillingError] = useState(null);
  const [billingResult, setBillingResult] = useState(null);
  const [billingLinkCopied, setBillingLinkCopied] = useState(false);

  const isAdmin = user && user.role === "admin";

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get("/admin/workspaces");
      setWorkspaces(res.data?.workspaces || []);
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || t("table.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchMetrics = useCallback(async () => {
    setMetricsError(null);
    try {
      const res = await api.get("/admin/metrics");
      setMetrics(res.data);
    } catch (e) {
      setMetricsError(formatApiErrorDetail(e.response?.data?.detail) || t("metrics.load_error"));
    }
  }, [t]);

  useEffect(() => {
    if (isAdmin) {
      fetchWorkspaces();
      fetchMetrics();
    }
  }, [isAdmin, fetchWorkspaces, fetchMetrics]);

  const kpis = useMemo(() => {
    const counters = { total: workspaces.length, insight: 0, guardian: 0, sentinel: 0 };
    workspaces.forEach((w) => {
      if (counters[w.effective_plan] !== undefined) counters[w.effective_plan] += 1;
    });
    return counters;
  }, [workspaces]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter(
      (w) =>
        (w.name || "").toLowerCase().includes(q) ||
        (w.owner_email || "").toLowerCase().includes(q),
    );
  }, [workspaces, query]);

  const handlePlanChange = async (ws, nextPlan) => {
    if (!ws || !nextPlan || nextPlan === ws.plan) return;
    setUpdatingPlanFor(ws.id);
    try {
      const res = await api.patch(`/admin/workspaces/${ws.id}/plan`, { plan: nextPlan });
      // effective_plan est derive cote backend (workspaces.effective_plan) et
      // n'est pas dans la reponse du PATCH : on relit la liste au lieu de
      // rejouer la regle d'entitlement ici.
      await fetchWorkspaces();
      toast.success(
        t("plan_update.success", {
          workspace: ws.name,
          previous: res.data.previous_plan,
          next: res.data.plan,
        }),
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || t("plan_update.error"),
      );
    } finally {
      setUpdatingPlanFor(null);
    }
  };

  const confirmSuspend = async () => {
    if (!pendingSuspend) return;
    const ws = pendingSuspend;
    setPendingSuspend(null);
    setUpdatingStatusFor(ws.id);
    try {
      const res = await api.post(`/admin/workspaces/${ws.id}/suspend`);
      setWorkspaces((prev) =>
        prev.map((row) => (row.id === ws.id ? { ...row, status: res.data.status } : row)),
      );
      toast.success(t("suspend_update.success_suspended", { workspace: ws.name }));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || t("suspend_update.error"),
      );
    } finally {
      setUpdatingStatusFor(null);
    }
  };

  const handleReactivate = async (ws) => {
    setUpdatingStatusFor(ws.id);
    try {
      const res = await api.post(`/admin/workspaces/${ws.id}/reactivate`);
      setWorkspaces((prev) =>
        prev.map((row) => (row.id === ws.id ? { ...row, status: res.data.status } : row)),
      );
      toast.success(t("suspend_update.success_reactivated", { workspace: ws.name }));
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e.response?.data?.detail) || t("suspend_update.error"),
      );
    } finally {
      setUpdatingStatusFor(null);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const name = formName.trim();
    const email = formEmail.trim();
    if (!name) {
      setFormError(t("create_form.error_invalid_name"));
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t("create_form.error_invalid_email"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/workspaces/create-and-invite", {
        workspace_name: name,
        owner_email: email,
        plan: formPlan,
      });
      const successKey = res.data.auto_accepted
        ? "create_form.success_auto"
        : "create_form.success_pending";
      toast.success(t(successKey, { name: res.data.workspace_name, email: res.data.owner_email }));
      setFormName("");
      setFormEmail("");
      setFormPlan("guardian");
      setShowCreateForm(false);
      await fetchWorkspaces();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || t("create_form.error_generic");
      setFormError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBillingModeChange = useCallback((mode) => {
    setBillingMode(mode);
    // A result/error generated in the other mode must not linger next to
    // inputs for this mode — otherwise an address typed for one prospect can
    // appear attached to a different customer's success message.
    setBillingResult(null);
    setBillingError(null);
    setBillingLinkCopied(false);
  }, []);

  const handleGenerateCheckoutLink = useCallback(async (e) => {
    e.preventDefault();
    setBillingSubmitting(true);
    setBillingError(null);
    setBillingResult(null);
    setBillingLinkCopied(false);
    try {
      const body = buildCheckoutLinkBody(billingMode, {
        workspaceId: billingWorkspaceId, plan: billingPlan, interval: billingInterval,
        email: billingEmail, company: billingCompany,
      });
      const res = await api.post("/admin/billing/checkout-link", body);
      setBillingResult(res.data);
    } catch (err) {
      setBillingError(t(checkoutLinkErrorKey(err)));
    } finally {
      setBillingSubmitting(false);
    }
  }, [billingMode, billingWorkspaceId, billingPlan, billingInterval,
      billingEmail, billingCompany, t]);

  const handleCopyBillingLink = useCallback(() => {
    if (!billingResult?.url) return;
    if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return;
    try {
      navigator.clipboard.writeText(billingResult.url)
        .then(() => setBillingLinkCopied(true))
        .catch(() => {
          // Clipboard permission denied or unavailable: the URL stays
          // selectable in the readOnly input, which is the real fallback.
        });
    } catch {
      // Non-secure context or unsupported API: same fallback as above.
    }
  }, [billingResult]);

  // ============================================================
  // Guard: non-admin users see a friendly forbidden state
  // ============================================================
  if (user && !isAdmin) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6" data-testid="admin-forbidden">
        <div className="rounded-2xl border border-zinc-200 bg-white p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
            <AlertTriangle size={26} className="text-rose-500" strokeWidth={1.75} />
          </div>
          <h1 className="text-xl font-semibold text-zinc-900 mb-2">
            {t("page.forbidden_title")}
          </h1>
          <p className="text-sm text-zinc-600">{t("page.forbidden_body")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-6 space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-zinc-900" data-testid="admin-page-title">
            {t("page.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">{t("page.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchWorkspaces}
            data-testid="admin-refresh-button"
            disabled={loading}
          >
            <RefreshCcw size={14} className={loading ? "animate-spin mr-1.5" : "mr-1.5"} />
            {t("actions.refresh")}
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCreateForm((v) => !v)}
            data-testid="admin-toggle-create-form"
          >
            {showCreateForm ? (
              <>
                <X size={14} className="mr-1.5" />
                {t("actions.close_create_form")}
              </>
            ) : (
              <>
                <Plus size={14} className="mr-1.5" />
                {t("actions.open_create_form")}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Platform metrics (Phase 1) */}
      <div data-testid="admin-metrics-section">
        <h2 className="text-xs uppercase tracking-[0.14em] text-zinc-500 mb-2">
          {t("metrics.title")}
        </h2>
        {metricsError ? (
          <div
            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
            data-testid="admin-metrics-error"
          >
            {metricsError}
          </div>
        ) : !metrics ? (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-metrics-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 rounded-xl border border-zinc-200 bg-zinc-50 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard
              label={t("metrics.total_users")}
              value={metrics.total_users}
              Icon={Users}
              testId="admin-metrics-kpi-total-users"
            />
            <KpiCard
              label={t("metrics.total_workspaces")}
              value={metrics.total_workspaces}
              Icon={Building2}
              testId="admin-metrics-kpi-total-workspaces"
            />
            <KpiCard
              label={t("metrics.signups_7d")}
              value={metrics.signups_last_7d}
              Icon={UserPlus}
              testId="admin-metrics-kpi-signups-7d"
            />
            <KpiCard
              label={t("metrics.signups_30d")}
              value={metrics.signups_last_30d}
              Icon={UserPlus}
              testId="admin-metrics-kpi-signups-30d"
            />
            <KpiCard
              label={t("metrics.documents_analyzed")}
              value={metrics.documents_analyzed}
              Icon={FileText}
              testId="admin-metrics-kpi-documents-analyzed"
            />
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label={t("kpis.workspaces")}
          value={kpis.total}
          Icon={Users}
          testId="admin-kpi-total"
        />
        <KpiCard
          label={t("kpis.insight")}
          value={kpis.insight}
          Icon={Sparkles}
          testId="admin-kpi-insight"
        />
        <KpiCard
          label={t("kpis.guardian")}
          value={kpis.guardian}
          Icon={ShieldCheck}
          testId="admin-kpi-guardian"
        />
        <KpiCard
          label={t("kpis.sentinel")}
          value={kpis.sentinel}
          Icon={Crown}
          testId="admin-kpi-sentinel"
        />
      </div>

      {/* Create form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateSubmit}
          className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4"
          data-testid="admin-create-workspace-form"
        >
          <div>
            <h2 className="text-base font-semibold text-zinc-900" data-testid="admin-create-form-title">
              {t("create_form.title")}
            </h2>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">{t("create_form.subtitle")}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5 md:col-span-1">
              <Label htmlFor="admin-form-name" className="text-xs">
                {t("create_form.workspace_name_label")}
              </Label>
              <Input
                id="admin-form-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={t("create_form.workspace_name_placeholder")}
                data-testid="admin-form-workspace-name"
                autoComplete="off"
                maxLength={80}
              />
            </div>
            <div className="space-y-1.5 md:col-span-1">
              <Label htmlFor="admin-form-email" className="text-xs">
                {t("create_form.owner_email_label")}
              </Label>
              <Input
                id="admin-form-email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder={t("create_form.owner_email_placeholder")}
                data-testid="admin-form-owner-email"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5 md:col-span-1">
              <Label className="text-xs">{t("create_form.plan_label")}</Label>
              <Select value={formPlan} onValueChange={setFormPlan}>
                <SelectTrigger data-testid="admin-form-plan-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insight" data-testid="admin-form-plan-option-insight">
                    {t("plans.insight")}
                  </SelectItem>
                  <SelectItem value="guardian" data-testid="admin-form-plan-option-guardian">
                    {t("plans.guardian")}
                  </SelectItem>
                  <SelectItem value="sentinel" data-testid="admin-form-plan-option-sentinel">
                    {t("plans.sentinel")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {formError && (
            <div
              className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              data-testid="admin-form-error"
            >
              {formError}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="admin-form-submit"
            >
              <Mail size={14} className="mr-1.5" />
              {submitting ? t("create_form.submitting") : t("create_form.submit")}
            </Button>
          </div>
        </form>
      )}

      {/* Billing checkout-link panel */}
      <form
        onSubmit={handleGenerateCheckoutLink}
        className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4"
        data-testid="admin-billing-panel"
      >
        <div>
          <h2 className="text-base font-semibold text-zinc-900" data-testid="admin-billing-title">
            {t("billing.title")}
          </h2>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">{t("billing.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={billingMode === "upgrade" ? "default" : "outline"}
            size="sm"
            onClick={() => handleBillingModeChange("upgrade")}
            data-testid="billing-mode-upgrade"
          >
            {t("billing.mode_upgrade")}
          </Button>
          <Button
            type="button"
            variant={billingMode === "new" ? "default" : "outline"}
            size="sm"
            onClick={() => handleBillingModeChange("new")}
            data-testid="billing-mode-new"
          >
            {t("billing.mode_new")}
          </Button>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {billingMode === "upgrade" ? (
            <div className="space-y-1.5 md:col-span-1">
              <Label className="text-xs">{t("billing.workspace")}</Label>
              <Select value={billingWorkspaceId} onValueChange={setBillingWorkspaceId}>
                <SelectTrigger data-testid="billing-workspace">
                  <SelectValue placeholder={t("billing.workspace")} />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map((ws) => (
                    <SelectItem
                      key={ws.id}
                      value={ws.id}
                      data-testid={`billing-workspace-option-${ws.id}`}
                    >
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="space-y-1.5 md:col-span-1">
                <Label htmlFor="admin-billing-email" className="text-xs">
                  {t("billing.owner_email")}
                </Label>
                <Input
                  id="admin-billing-email"
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder={t("create_form.owner_email_placeholder")}
                  data-testid="billing-owner-email"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1.5 md:col-span-1">
                <Label htmlFor="admin-billing-company" className="text-xs">
                  {t("billing.workspace_name")}
                </Label>
                <Input
                  id="admin-billing-company"
                  value={billingCompany}
                  onChange={(e) => setBillingCompany(e.target.value)}
                  placeholder={t("create_form.workspace_name_placeholder")}
                  data-testid="billing-workspace-name"
                  autoComplete="off"
                  maxLength={80}
                />
              </div>
            </>
          )}

          <div className="space-y-1.5 md:col-span-1">
            <Label className="text-xs">{t("billing.plan")}</Label>
            <Select value={billingPlan} onValueChange={setBillingPlan}>
              <SelectTrigger data-testid="billing-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="insight" data-testid="billing-plan-option-insight">
                  {t("plans.insight")}
                </SelectItem>
                <SelectItem value="guardian" data-testid="billing-plan-option-guardian">
                  {t("plans.guardian")}
                </SelectItem>
                <SelectItem value="sentinel" data-testid="billing-plan-option-sentinel">
                  {t("plans.sentinel")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">{t("billing.interval")}</Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant={billingInterval === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => setBillingInterval("month")}
              data-testid="billing-interval-month"
            >
              {t("billing.interval_month")}
            </Button>
            <Button
              type="button"
              variant={billingInterval === "year" ? "default" : "outline"}
              size="sm"
              onClick={() => setBillingInterval("year")}
              data-testid="billing-interval-year"
            >
              {t("billing.interval_year")}
            </Button>
          </div>
        </div>

        {billingError && (
          <div
            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
            data-testid="admin-billing-error"
          >
            {billingError}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={billingSubmitting}
            data-testid="billing-submit"
          >
            <Mail size={14} className="mr-1.5" />
            {billingSubmitting ? t("billing.submitting") : t("billing.submit")}
          </Button>
        </div>

        {billingResult && (
          <div
            className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-3 space-y-2"
            data-testid="billing-link-result"
          >
            <p className="text-xs font-medium text-zinc-700">{t("billing.link_ready")}</p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={billingResult.url}
                className="text-xs"
                data-testid="billing-link-url"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyBillingLink}
                data-testid="billing-link-copy"
              >
                {billingLinkCopied ? t("billing.copied") : t("billing.copy")}
              </Button>
            </div>
            <p className="text-xs text-zinc-500">
              {t("billing.expires", {
                date: formatDate(
                  new Date(billingResult.expires_at * 1000).toISOString(),
                  i18n.language,
                ),
              })}
            </p>
            <p className="text-xs text-zinc-500">
              {billingResult.email_sent
                ? t("billing.email_sent", { email: billingResult.recipient })
                : t("billing.email_not_sent")}
            </p>
          </div>
        )}
      </form>

      {/* Toolbar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            className="pl-8"
            placeholder={t("table.search_placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        {loadError ? (
          <div
            className="px-6 py-10 text-center text-sm text-rose-700"
            data-testid="admin-load-error"
          >
            {loadError}
          </div>
        ) : loading ? (
          <div
            className="px-6 py-10 text-center text-sm text-zinc-500"
            data-testid="admin-loading"
          >
            {t("table.loading")}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="px-6 py-10 text-center text-sm text-zinc-500"
            data-testid="admin-empty"
          >
            {t("table.empty")}
          </div>
        ) : (
          <Table data-testid="admin-workspaces-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("table.columns.name")}</TableHead>
                <TableHead>{t("table.columns.owner")}</TableHead>
                <TableHead>{t("table.columns.plan")}</TableHead>
                <TableHead>{t("table.columns.status")}</TableHead>
                <TableHead>{t("billing.column")}</TableHead>
                <TableHead className="text-center">{t("table.columns.members")}</TableHead>
                <TableHead className="text-center">{t("table.columns.documents")}</TableHead>
                <TableHead>{t("table.columns.created")}</TableHead>
                <TableHead>{t("table.columns.last_active")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((ws) => (
                <TableRow key={ws.id} data-testid={`admin-workspace-row-${ws.id}`}>
                  <TableCell className="max-w-[220px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="font-medium text-zinc-900 truncate"
                        data-testid={`admin-workspace-name-${ws.id}`}
                        title={ws.name}
                      >
                        {ws.name}
                      </span>
                      {ws.is_personal && (
                        <span className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-200 rounded px-1.5 py-0.5">
                          {t("table.personal_badge")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-700" data-testid={`admin-workspace-owner-${ws.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate" title={ws.owner_email || ""}>
                        {ws.owner_email || "—"}
                      </span>
                      {ws.owner_pending && (
                        <span
                          className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap"
                          data-testid={`admin-workspace-pending-${ws.id}`}
                        >
                          {t("table.pending_owner_badge")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <PlanBadge plan={ws.effective_plan} />
                      <Select
                        value={ws.plan}
                        onValueChange={(v) => handlePlanChange(ws, v)}
                        disabled={updatingPlanFor === ws.id}
                      >
                        <SelectTrigger
                          className="h-8 w-[130px] text-xs"
                          data-testid={`admin-plan-select-${ws.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="insight" data-testid={`admin-plan-option-insight-${ws.id}`}>
                            {t("plans.insight")}
                          </SelectItem>
                          <SelectItem value="guardian" data-testid={`admin-plan-option-guardian-${ws.id}`}>
                            {t("plans.guardian")}
                          </SelectItem>
                          <SelectItem value="sentinel" data-testid={`admin-plan-option-sentinel-${ws.id}`}>
                            {t("plans.sentinel")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {ws.effective_plan !== ws.plan && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium text-amber-700 bg-amber-50 border-amber-200 whitespace-nowrap"
                          data-testid={`admin-plan-degraded-${ws.id}`}
                          title={t("table.degraded_title", {
                            stored: ws.plan,
                            effective: ws.effective_plan,
                          })}
                        >
                          <AlertTriangle size={10} strokeWidth={2.5} />
                          {t("table.degraded_badge")}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={ws.status} t={t} />
                      {ws.status === "suspended" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => handleReactivate(ws)}
                          disabled={updatingStatusFor === ws.id}
                          data-testid={`admin-reactivate-button-${ws.id}`}
                        >
                          <PlayCircle size={12} className="mr-1.5" />
                          {t("actions.reactivate")}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50"
                          onClick={() => setPendingSuspend(ws)}
                          disabled={
                            updatingStatusFor === ws.id ||
                            (ws.is_personal && ws.owner_id === user?.id)
                          }
                          title={
                            ws.is_personal && ws.owner_id === user?.id
                              ? t("suspend_update.self_error")
                              : undefined
                          }
                          data-testid={`admin-suspend-button-${ws.id}`}
                        >
                          <Ban size={12} className="mr-1.5" />
                          {t("actions.suspend")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell data-testid={`admin-workspace-subscription-${ws.id}`}>
                    <SubscriptionBadge
                      status={ws.subscription_status}
                      periodEnd={ws.current_period_end}
                      locale={i18n.language}
                      t={t}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-zinc-700">
                      <Users size={12} className="text-zinc-400" />
                      {ws.member_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-zinc-700">
                      <FileText size={12} className="text-zinc-400" />
                      {ws.document_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {formatDate(ws.created_at, i18n.language)}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {formatDate(ws.last_active_at, i18n.language)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <AlertDialog open={!!pendingSuspend} onOpenChange={(o) => !o && setPendingSuspend(null)}>
        <AlertDialogContent data-testid="admin-suspend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("suspend_dialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("suspend_dialog.body", { workspace: pendingSuspend?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-suspend-cancel">
              {t("suspend_dialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSuspend}
              data-testid="admin-suspend-confirm"
              className="bg-rose-600 hover:bg-rose-700"
            >
              {t("suspend_dialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
